import React, { useState } from 'react';
import cx from 'classnames';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { v4 as uuidv4 } from 'uuid';

import CardLayout from '@magoosh/layouts/card';
import { BannerAlert } from '@magoosh/lib/alerts';
import { SuccessBadge, WarningBadge } from '@magoosh/lib/badge';
import { GradientButton } from '@magoosh/lib/button';
import { FontAwesomeIcon, SparkleIcon } from '@magoosh/lib/icons';
import PremiumBadge from '@magoosh/lib/info_badges';
import AudioRecorder from '@magoosh/organisms/audio/recorder';
import * as ContentNodes from '@magoosh/organisms/content_nodes'
import useSearchQuery from '@magoosh/utils/use_search_query';
import { paths } from 'config/path_helpers';
import { pluralize } from 'utilities/pluralize';

import * as api from '@test_prep/app/api';
import ContextualLink from '@test_prep/app/components/contextual_link';
import { useAppContext } from '@test_prep/app/context';
import { useQueries } from '@test_prep/app/queries';

import RetryPolling from './retry_polling';
import Response from '../components/response';
import ScoreInfluenceAwareContent from '../components/score_influence_aware_content';
import { ScoreReportResponse } from '../types';

import colors from '@magoosh/lib/styles/colors.module.scss';

export default (props) => {
  const { branding, settings } = useAppContext().data;
  const { id, answerId } = useParams();
  const q = useQueries();
  const queryClient = useQueryClient();
  const returnToAnswerId = useSearchQuery()['return_to_answer_id'];

  // Set this key to reset the practice interface
  const [practiceKey, setPracticeKey] = useState<string>(uuidv4());
  const [gradingInProgress, setGradingInProgress] = useState<boolean>(false);
  const [retryBeingGraded, setRetryBeingGraded] = useState<ScoreReportResponse>(null);
  const [volume, setVolume] = useState<number>(1);

  const pagePath = paths.scoreReportRetryAnswer(id, answerId);
  const pageQuery = q.pageQuery(pagePath, { keepPreviousData: true, refetchOnMount: 'always' });
  const page = pageQuery.data;
  const retries: ScoreReportResponse[] = page?.response?.retries ?? [];

  const backToScoreReportPath = () => {
    if (returnToAnswerId) {
      return paths.scoreReportAnswer(id, parseInt(returnToAnswerId as string));
    } else {
      return paths.scoreReport(id);
    }
  };

  const handleResponseRetry = (blob) => {
    const originalRetries = retries;

    // Optimistically set the UI state to give instant visual feedback
    updateCachedRetries([
      {
        answer: {
          prompt: page.response.answer.prompt,
          response: {
            audio: {}
          }
        },
        content: { facets: null, narrative: null },
        id: null,
        score: null,
        scoreInfluence: null,
        scoreRange: page.response.scoreRange,
        status: 'processing'
      } as any,
      ...originalRetries
    ]);
    setGradingInProgress(true);

    api.createGradingRequestResponseRetry(page.response.id, blob).then((responseRetry) => {
      updateCachedRetries([responseRetry, ...originalRetries]);
      setRetryBeingGraded(responseRetry);
    });
  };

  const handleRetryUpdated = (updatedRetry) => {
    updateCachedRetries(retries.map((r) => r.id === updatedRetry.id ? updatedRetry : r));

    if (updatedRetry.status === 'finished' || updatedRetry.status === 'error') {
      setGradingInProgress(false);
      setPracticeKey(uuidv4());
      setRetryBeingGraded(null);
    }
  };

  const regrade = (retry: ScoreReportResponse) => {
    updateCachedRetries(retries.map((r) => r.id === retry.id ? { ...r, status: 'processing' } : r));
    setRetryBeingGraded(retry);
    api.regradeGradingRequestResponseRetry(page.response.id, retry.id);
  };

  const renderPractice = () => {
    const promptStructuredData = page.response.answer.prompt.content.structuredData
    const componentProps = promptStructuredData.props;
    // Should autoplay be a standard prop and then we override it here?
    componentProps.stimulus.noAutoplay = true
    componentProps.compatibilityMode = settings?.stimulusCompatibilityMode;

    const ContentNodeComponent = ContentNodes[
      promptStructuredData.component as keyof typeof ContentNodes
    ];

    return (
      <div key={practiceKey} className="d-flex flex-column flex-sm-row gap-m">
        <div className="flex-1">
          <ContentNodeComponent {...componentProps} />
        </div>

        <div className="d-flex flex-1 flex-column gap-l">
          {/* Should we include the AudioPlayer in the StimulusWithAudioRecord component? */}
          <AudioRecorder
            onBlobAvailable={handleResponseRetry}
            prepTime={page.response.answer.response.audio.prepTime}
            recordingTime={page.response.answer.response.audio.recordingTime}
          />

          {
            gradingInProgress && (
              <div className="d-flex flex-column gap-m align-items-center">
                <div
                  style={{
                    background: colors.grayLightest,
                    border: `2px solid ${colors.grayLight}`,
                    borderRadius: 8,
                    padding: '24px 31px'
                  }}
                >
                  <FontAwesomeIcon icon="microphone" style={{ color: colors.grayLight, fontSize: 40 }} />
                </div>

                <div className="align-items-center d-flex flex-column gap-xs">
                  <strong>Analyzing your recording</strong>
                  <div>This will take just a few seconds</div>
                </div>
              </div>
            )
          }
        </div>
      </div>
    );
  };

  const updateCachedRetries = (newRetries: ScoreReportResponse[]) => {
    queryClient.setQueryData(['pages', pagePath], (oldData: any) => ({
      ...oldData,
      response: { ...oldData.response, retries: newRetries }
    }));
  };

  return (
    <>
      {
        retryBeingGraded && (
          <RetryPolling
            responseId={page.response.id}
            retryId={retryBeingGraded.id}
            onUpdated={handleRetryUpdated}
          />
        )
      }

      <Helmet>
        <title>
          Score Report - Try Your Response Again
        </title>
      </Helmet>

      <CardLayout
        linkComponent={ContextualLink}
        loading={!page}
        subnav={{
          title: 'Try Your Response Again',
          backLink: { name: 'Back to score report', path: backToScoreReportPath() }
        }}
      >
        {({ loading }) => {
          if (loading) return;

          return (
            <div className="d-flex flex-column gap-m">
              {
                branding.accessLevel === 'premium' ? (
                  <CardLayout.Card className="d-flex flex-column gap-m">
                    {renderPractice()}
                  </CardLayout.Card>
                ) : (
                  <BannerAlert>
                    <PremiumBadge /> Upgrade to premium to retry your response and get feedback until you get the score you're aiming for.
                  </BannerAlert>
                )
              }

              <div className="d-flex flex-column gap-s gap-lg-m">
                <h3 className="flex-1 u-margin-A-n u-margin-T-s">
                  <strong>Your Attempts</strong>
                </h3>

                <hr className="u-margin-A-n" />
              </div>

              
              {
                retries.length >= 1 && (
                  <>
                    {
                      retries.map((retry, idx) => {
                        let scoreBadge = null;

                        if (retry.score && retry.score > page.response.score) {
                          scoreBadge = {
                            text: `+${pluralize(retry.score - page.response.score, 'point')}`,
                            type: 'positive'
                          }
                        }

                        return (
                          <CardLayout.Card key={retry.id} borderGradient={idx === 0}>
                            <Response
                              regrade={regrade}
                              response={retry}
                              scoreBadge={scoreBadge}
                              setVolume={setVolume}
                              title={`Attempt ${retries.length - idx}${idx === 0 ? ' (Latest)' : ''}`}
                              volume={volume}
                            />
                          </CardLayout.Card>
                        )
                      })
                    }
                  </>
                )
              }

              <CardLayout.Card className="d-flex flex-column gap-m">
                <Response
                  response={page.response}
                  setVolume={setVolume}
                  title="Original"
                  volume={volume}
                />
              </CardLayout.Card>
            </div>
          );
        }}
      </CardLayout>
    </>
  );
};
